import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "../components/Navigation";
import LoadingSpinner from "../components/LoadingSpinner";
import Card from "../components/Card";
import Button from "../components/Buttons";
import TPRMNewVendorModal from "../components/TPRMNewVendorModal";
import VendorPerformanceChart from "../components/VendorPerformanceChart";
import { ChevronLeft, Plus } from "lucide-react";
import { severityPostureScore } from "../utils/riskScoringEngine";

const TIER_COLORS = { "Tier 1": "#ef4444", "Tier 2": "#f59e0b", "Tier 3": "#22c55e", "None": "#9ca3af" };
const RISK_COLORS = { CRITICAL: "#b91c1c", HIGH: "#ea580c", MEDIUM: "#eab308", LOW: "#16a34a" };

const PERF_STATUS_CFG = {
    Pending: "bg-yellow-100 text-yellow-800",
    Overdue: "bg-red-100 text-red-800",
};
const PERF_LABEL_CFG = {
    "Preferred Vendor":  "bg-green-100 text-green-800",
    "Strategic Vendor":  "bg-emerald-50 text-emerald-700",
    "Acceptable":        "bg-blue-50 text-blue-700",
    "Needs Improvement": "bg-yellow-100 text-yellow-800",
    "High Concern":      "bg-red-100 text-red-800",
};

// TPRM assessment status labels/colors for the Assessment column (UN EOI Phase 1 due dates).
const ASSESS_STATUS_CFG = {
    Pending:        { label: "Pending",     cls: "bg-yellow-100 text-yellow-800" },
    InProgress:     { label: "In Progress", cls: "bg-blue-100 text-blue-800" },
    Analyzing:      { label: "Analyzing",   cls: "bg-indigo-100 text-indigo-800" },
    AnalysisFailed: { label: "Failed",      cls: "bg-red-100 text-red-800" },
    Completed:      { label: "Completed",   cls: "bg-green-100 text-green-800" },
};

const formatShortDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const formatCurrency = (val) => {
    if (val == null) return "--";
    const n = Number(val);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n).toLocaleString()}`;
};

const getRiskLevelColor = (level) => {
    switch (level) {
        case "CRITICAL": return "text-red-700 bg-red-100";
        case "HIGH": return "text-orange-600 bg-orange-100";
        case "MEDIUM": return "text-yellow-600 bg-yellow-100";
        case "LOW": return "text-green-600 bg-green-100";
        default: return "text-gray-600 bg-gray-100";
    }
};

const getTierBadgeColor = (tier) => {
    switch (tier) {
        case 1: return "bg-red-100 text-red-800";
        case 2: return "bg-yellow-100 text-yellow-800";
        case 3: return "bg-green-100 text-green-800";
        default: return "bg-gray-100 text-gray-600";
    }
};

const CISOPortfolioView = () => {
    const [portfolio, setPortfolio] = useState(null);
    const [perf, setPerf] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortField, setSortField] = useState("riskScorePrecise");
    const [sortDir, setSortDir] = useState("desc");
    const [showNewVendorModal, setShowNewVendorModal] = useState(false);
    const apiCall = useApi();
    const navigate = useNavigate();
    const { canCreateVendor } = useRBAC();
    const [searchParams] = useSearchParams();
    const dealId = searchParams.get("dealId");
    const businessUnitId = searchParams.get("businessUnitId");
    const isDealScoped = !!dealId;
    const isBUScoped = !!businessUnitId;
    const canAdd = canCreateVendor();

    const fetchPortfolio = useCallback(async () => {
        setLoading(true);
        try {
            const params = dealId ? `?dealId=${dealId}` : businessUnitId ? `?businessUnitId=${businessUnitId}` : "";
            const url = `/TPRMVendor/portfolio-summary${params}`;
            // The performance-stats endpoint takes the same deal/BU scoping, so the summary
            // card's figures always describe exactly the vendors listed in the table below.
            const [data, perfData] = await Promise.all([
                apiCall("GET", url),
                apiCall("GET", `/tprm-performance-scorecards/portfolio${params}`).catch(() => null),
            ]);
            setPortfolio(data);
            setPerf(perfData);
        } catch (err) {} finally {
            setLoading(false);
        }
    }, [dealId, businessUnitId, apiCall]);

    useEffect(() => {
        fetchPortfolio();
    }, [fetchPortfolio]);

    if (loading) return <LoadingSpinner />;

    if (!portfolio) {
        return (
            <div className="min-h-screen bg-theme-primary">
                <Navigation />
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <p className="text-gray-500">Failed to load portfolio data.</p>
                </div>
            </div>
        );
    }

    // Derive the 1-decimal posture score from the severity counts (same engine the backend uses), so
    // display + sort are correct regardless of whether the API populates riskScorePrecise.
    // A vendor with no findings and no completed assessment has not been assessed: its 0 score
    // means "unknown", not "safe", so it shows as Not Assessed and sorts after the assessed vendors
    // whichever way the column is sorted (AX-16).
    const vendorRows = (portfolio.top10RiskiestVendors || []).map(v => ({
        ...v,
        riskScorePrecise: severityPostureScore({
            CRITICAL: v.criticalCount || 0,
            HIGH: v.highCount || 0,
            MEDIUM: v.mediumCount || 0,
            LOW: v.lowCount || 0,
        }),
        notAssessed: (v.totalRisks || 0) === 0 && v.assessmentStatus !== "Completed",
    }));

    const sortedVendors = [...vendorRows].sort((a, b) => {
        if (a.notAssessed !== b.notAssessed) return a.notAssessed ? 1 : -1;
        const aVal = a[sortField] ?? 0;
        const bVal = b[sortField] ?? 0;
        return sortDir === "desc" ? bVal - aVal : aVal - bVal;
    });
    const assessedVendorCount = vendorRows.filter(v => !v.notAssessed).length;
    const notAssessedVendorCount = vendorRows.length - assessedVendorCount;
    // Share of total EAL is only a meaningful concentration figure with two or more vendors carrying
    // exposure; with one it is always 100%.
    const vendorsWithExposure = vendorRows.filter(v => v.totalExpectedAnnualLoss > 0).length;

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === "desc" ? "asc" : "desc");
        } else {
            setSortField(field);
            setSortDir("desc");
        }
    };

    const tierData = [
        { name: "Tier 1", value: portfolio.tierDistribution?.tier1Count || 0 },
        { name: "Tier 2", value: portfolio.tierDistribution?.tier2Count || 0 },
        { name: "Tier 3", value: portfolio.tierDistribution?.tier3Count || 0 },
        { name: "None", value: portfolio.tierDistribution?.untieredCount || 0 },
    ].filter(d => d.value > 0);

    const ealData = [...(portfolio.top10RiskiestVendors || [])]
        .filter(v => v.totalExpectedAnnualLoss > 0 || v.conservativeAnnualLoss > 0)
        .sort((a, b) => b.totalExpectedAnnualLoss - a.totalExpectedAnnualLoss)
        .slice(0, 5)
        .map(v => ({
            name: v.vendorName.length > 18 ? v.vendorName.slice(0, 18) + "…" : v.vendorName,
            conservative: v.conservativeAnnualLoss || 0,
            base: v.totalExpectedAnnualLoss || 0,
        }));

    // Derived from sortedVendors (one row per vendor, already deduped) rather than the portfolio
    // endpoint's raw scorecard lists, so these counts can never exceed the vendor count and always
    // match the Vendors table below.
    // "Reviewed" = has a completed score on file, regardless of current status — a vendor with a
    // NEW cycle issued (performanceStatus flips back to Pending) still keeps its prior score.
    // Every other stat here (pending / overdue / due-soon) describes vendors that have NEVER been
    // reviewed, so the whole card stays internally consistent: reviewed + pending = vendor count,
    // and overdue/due-soon are always subsets of pending, never a separate universe that
    // re-includes an already-reviewed vendor.
    const neverReviewed = sortedVendors.filter(v => v.performanceScore == null);
    const perfReviewedCount = sortedVendors.length - neverReviewed.length;
    const perfPendingCount = neverReviewed.filter(v =>
        v.performanceStatus === "Pending" || v.performanceStatus === "Overdue").length;
    const perfOverdueCount = neverReviewed.filter(v => v.performanceStatus === "Overdue").length;
    const perfDueSoonCount = neverReviewed.filter(v => {
        if (v.performanceStatus !== "Pending" || !v.performanceDueDate) return false;
        const daysUntilDue = (new Date(v.performanceDueDate) - new Date()) / 86_400_000;
        return daysUntilDue <= 30;
    }).length;

    // Hide the performance card entirely when no review has ever run — avoids an empty-state
    // tile cluttering the charts row for orgs that haven't adopted scorecards yet.
    const showPerf = !!perf && (perfReviewedCount > 0 || perfPendingCount > 0);

    const riskBreakdownData = [...(portfolio.top10RiskiestVendors || [])]
        .filter(v => v.totalRisks > 0)
        .sort((a, b) => b.totalRisks - a.totalRisks)
        .slice(0, 5)
        .map(v => ({
            name: v.vendorName.length > 18 ? v.vendorName.slice(0, 18) + "…" : v.vendorName,
            Critical: v.criticalCount || 0,
            High: v.highCount || 0,
            Medium: v.mediumCount || 0,
            Low: v.lowCount || 0,
        }));

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <div className="mb-6">
                    {isDealScoped && (
                        <button
                            onClick={() => navigate(`/TPRMDeals?dealId=${dealId}`)}
                            className="flex items-center text-sm text-blue-600 hover:text-blue-800 mb-2"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back to deal
                        </button>
                    )}
                    {isBUScoped && (
                        <button
                            onClick={() => navigate(`/TPRMBusinessUnits?businessUnitId=${businessUnitId}`)}
                            className="flex items-center text-sm text-blue-600 hover:text-blue-800 mb-2"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back to business unit
                        </button>
                    )}
                    <h1 className="text-3xl font-bold text-content-primary">
                        {isDealScoped
                            ? `M&A Vendor Risk Report — ${portfolio.dealName || "Deal"}`
                            : isBUScoped
                            ? `Business Unit Vendor Risk Report — ${portfolio.businessUnitName || "Business Unit"}`
                            : "Vendor Portfolio"}
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                        {isDealScoped
                            ? "Vendor risk exposure for acquisition target"
                            : isBUScoped
                            ? "Vendor risk exposure for business unit"
                            : "Executive overview of third-party vendor risk exposure"}
                    </p>
                </div>

                {/* Aggregate Exposure Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
                    <Card className="text-center">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Vendors</div>
                        <div className="text-3xl font-bold text-content-primary">{portfolio.aggregateExposure?.totalVendors || 0}</div>
                    </Card>
                    <Card className="text-center">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Active Risks</div>
                        <div className="text-3xl font-bold text-content-primary">{portfolio.aggregateExposure?.totalRisks || 0}</div>
                    </Card>
                    <Card className="text-center">
                        <div className="text-xs font-semibold text-emerald-600 uppercase mb-1">Expected Annual Loss</div>
                        <div className="text-3xl font-bold text-emerald-800">{formatCurrency(portfolio.aggregateExposure?.totalExpectedAnnualLoss)}</div>
                    </Card>
                    <Card className="text-center">
                        <div data-testid="portfolio-coverage-tile">
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Assessment Coverage</div>
                            <div className="text-3xl font-bold text-content-primary">
                                {assessedVendorCount} of {vendorRows.length}
                            </div>
                            <div className={`text-xs mt-1 ${notAssessedVendorCount > 0 ? "text-amber-700 font-medium" : "text-gray-500"}`}>
                                {notAssessedVendorCount > 0
                                    ? `${notAssessedVendorCount} vendor${notAssessedVendorCount === 1 ? "" : "s"} not assessed`
                                    : "Every vendor assessed"}
                            </div>
                        </div>
                    </Card>
                    <Card className="text-center">
                        <div data-testid="portfolio-concentration-tile">
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Exposure Concentration</div>
                            {vendorsWithExposure < 2 ? (
                                <>
                                    <div className="text-lg font-bold text-gray-400">n/a</div>
                                    <div className="text-xs text-gray-500 mt-1">Needs two or more vendors with expected loss</div>
                                </>
                            ) : (
                                <>
                                    <div className={`text-lg font-bold ${
                                        portfolio.concentrationMetrics?.concentrationLevel === "High" ? "text-red-700" :
                                        portfolio.concentrationMetrics?.concentrationLevel === "Moderate" ? "text-yellow-700" :
                                        "text-green-700"
                                    }`}>
                                        {portfolio.concentrationMetrics?.concentrationLevel || "Low"}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Top vendor: {portfolio.concentrationMetrics?.topVendorPercentOfTotal?.toFixed(0)}% of total EAL
                                    </div>
                                </>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Scenario Bands */}
                <Card className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Aggregate Scenario Bands</h3>
                    <p className="text-sm text-gray-500 mb-4">Estimated aggregate financial exposure to your organization across all vendors, calculated using FAIR-aligned methodology. The conservative scenario reflects a lower-bound estimate under favorable conditions, while the severe scenario models a worst-case outcome with compounding losses across multiple vendors.</p>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200 text-center">
                            <div className="text-xs font-semibold text-green-600 uppercase mb-1">Conservative</div>
                            <div className="text-2xl font-bold text-green-800">{formatCurrency(portfolio.aggregateExposure?.scenarioBands?.conservative)}</div>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                            <div className="text-xs font-semibold text-blue-600 uppercase mb-1">Base Case</div>
                            <div className="text-2xl font-bold text-blue-800">{formatCurrency(portfolio.aggregateExposure?.scenarioBands?.baseCase)}</div>
                        </div>
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200 text-center">
                            <div className="text-xs font-semibold text-red-600 uppercase mb-1">Severe</div>
                            <div className="text-2xl font-bold text-red-800">{formatCurrency(portfolio.aggregateExposure?.scenarioBands?.severe)}+</div>
                        </div>
                    </div>
                </Card>

                {/* Charts Row */}
                <div className={`grid grid-cols-1 md:grid-cols-2 ${showPerf ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-6 mb-6`}>
                    {/* Tier Distribution */}
                    <Card>
                        <h3 className="text-lg font-semibold mb-4">Tier Distribution</h3>
                        {tierData.length > 0 ? (
                            <>
                                {/* No outside label/labelLine — Recharts positions an outside pie label
                                    along the slice's own midpoint angle, which for a majority (or, worse,
                                    a single 100%) slice can point straight out toward the card edge with
                                    no room left regardless of chart margin (tried: still clipped, e.g.
                                    "None (5)" rendering as just "e (5)"). A plain-text legend below the
                                    ring can't clip the same way — it just wraps/flows like any other
                                    text — so it's the reliable fix, not another margin/radius guess. */}
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart margin={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                                        <Pie data={tierData} cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                                            paddingAngle={3} dataKey="value" minAngle={5}>
                                            {tierData.map((entry) => (
                                                <Cell key={entry.name} fill={TIER_COLORS[entry.name]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
                                    {tierData.map(entry => (
                                        <span key={entry.name} className="flex items-center gap-1.5 text-xs text-gray-600">
                                            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: TIER_COLORS[entry.name] }} />
                                            {entry.name} ({entry.value})
                                        </span>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="text-gray-500 text-sm">No tier data available.</p>
                        )}
                    </Card>

                    {/* EAL by Vendor */}
                    <Card>
                        <h3 className="text-lg font-semibold mb-4">Top Vendors by Annual Loss</h3>
                        {ealData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={ealData} layout="vertical" margin={{ left: 10, right: 10 }}
                                    barCategoryGap={ealData.length === 1 ? "60%" : "20%"}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" tickFormatter={formatCurrency} />
                                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                    <Legend />
                                    <Bar dataKey="conservative" fill="#22c55e" name="Conservative" maxBarSize={40} />
                                    <Bar dataKey="base" fill="#3b82f6" name="Base Case" maxBarSize={40} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="text-gray-500 text-sm">No financial data available.</p>
                        )}
                    </Card>

                    {/* Vendor Risk Breakdown */}
                    <Card>
                        <h3 className="text-lg font-semibold mb-4">Vendor Risk Breakdown</h3>
                        {riskBreakdownData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={riskBreakdownData} margin={{ left: -10, right: 10, bottom: 5 }}
                                    barCategoryGap={riskBreakdownData.length === 1 ? "60%" : "20%"}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="category" dataKey="name" tick={{ fontSize: 10, angle: -25, textAnchor: "end" }} interval={0} height={50} />
                                    <YAxis type="number" allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="Critical" stackId="risks" fill="#b91c1c" maxBarSize={50} />
                                    <Bar dataKey="High" stackId="risks" fill="#ea580c" maxBarSize={50} />
                                    <Bar dataKey="Medium" stackId="risks" fill="#eab308" maxBarSize={50} />
                                    <Bar dataKey="Low" stackId="risks" fill="#16a34a" maxBarSize={50} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="text-gray-500 text-sm">No risk data available.</p>
                        )}
                    </Card>

                    {/* Vendor Performance Summary — business-owner scorecards (UN EOI reporting).
                        Chart leads: one bar per vendor, colored by recommendation band, worst
                        first — severity readable at a glance; the aggregate stats sit below. */}
                    {showPerf && (
                        <Card>
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <h3 className="text-lg font-semibold">Vendor Performance</h3>
                                {/* Each phrase is unbreakable — a wrap lands between "reviewed ·"
                                    and "N pending", never between a number and its word. */}
                                <span className="text-xs text-gray-400 text-right">
                                    <span className="whitespace-nowrap">{perfReviewedCount} reviewed</span>
                                    {" · "}
                                    <span className={`whitespace-nowrap ${perfPendingCount > 0 ? "text-amber-600 font-semibold" : ""}`}>
                                        {perfPendingCount} pending
                                    </span>
                                </span>
                            </div>
                            {/* Capped at the sibling chart cards' content height (280): stats
                                follow the chart directly; any slack falls below, like the
                                other cards' internal whitespace. */}
                            <div className="flex flex-col" style={{ maxHeight: 280 }}>
                                <VendorPerformanceChart
                                    vendors={sortedVendors}
                                    onVendorClick={(vendorId) => navigate(`/VendorDashboard/${vendorId}`)}
                                />
                                <div className="grid grid-cols-4 gap-2 mt-5 pt-3 border-t border-gray-100">
                                    <div title="Average of each reviewed vendor's latest score">
                                        <div className="text-[9px] text-gray-400 uppercase tracking-wide leading-tight">Avg Score</div>
                                        <div className="text-base font-bold text-indigo-700">
                                            {perf.averageScore != null ? Number(perf.averageScore).toFixed(1) : "—"}
                                            <span className="text-[10px] text-gray-400 font-normal"> / 5</span>
                                        </div>
                                    </div>
                                    <div title="Vendors improving / declining vs their previous review">
                                        <div className="text-[9px] text-gray-400 uppercase tracking-wide leading-tight">Impr / Decl</div>
                                        <div className="text-base font-bold">
                                            <span className="text-green-600">{perf.improvingVendors}</span>
                                            <span className="text-gray-300 mx-0.5">/</span>
                                            <span className="text-red-600">{perf.decliningVendors}</span>
                                        </div>
                                    </div>
                                    <div title="Never-reviewed vendors whose review is past due">
                                        <div className="text-[9px] text-gray-400 uppercase tracking-wide leading-tight">Overdue</div>
                                        <div className={`text-base font-bold ${perfOverdueCount > 0 ? "text-red-600" : "text-gray-700"}`}>
                                            {perfOverdueCount}
                                        </div>
                                    </div>
                                    <div title="Never-reviewed vendors due within 30 days">
                                        <div className="text-[9px] text-gray-400 uppercase tracking-wide leading-tight">Due 30 Days</div>
                                        <div className={`text-base font-bold ${perfDueSoonCount > 0 ? "text-amber-600" : "text-gray-700"}`}>
                                            {perfDueSoonCount}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}
                </div>

                {/* Riskiest Vendors Table */}
                <Card>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">
                            {(isDealScoped || isBUScoped) ? "Vendors by Risk" : "Vendors"}
                        </h3>
                        {canAdd && (
                            <Button size="sm" onClick={() => setShowNewVendorModal(true)}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add Vendor
                            </Button>
                        )}
                    </div>
                    {sortedVendors.length === 0 ? (
                        <p className="text-gray-500 text-sm">No vendor risk data available.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-2 px-3 font-medium text-gray-600">Vendor</th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600">Tier</th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600 cursor-pointer select-none"
                                            onClick={() => toggleSort("riskScorePrecise")}>
                                            Risk Score {sortField === "riskScorePrecise" ? (sortDir === "desc" ? "\u2193" : "\u2191") : ""}
                                        </th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600 cursor-pointer select-none"
                                            onClick={() => toggleSort("totalRisks")}>
                                            Active Risks {sortField === "totalRisks" ? (sortDir === "desc" ? "\u2193" : "\u2191") : ""}
                                        </th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600 cursor-pointer select-none"
                                            onClick={() => toggleSort("totalExpectedAnnualLoss")}>
                                            EAL {sortField === "totalExpectedAnnualLoss" ? (sortDir === "desc" ? "\u2193" : "\u2191") : ""}
                                        </th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600 cursor-pointer select-none"
                                            onClick={() => toggleSort("axarisVendorImpactIndex")}>
                                            AVI {sortField === "axarisVendorImpactIndex" ? (sortDir === "desc" ? "\u2193" : "\u2191") : ""}
                                        </th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600 cursor-pointer select-none"
                                            onClick={() => toggleSort("ransomwarePredictionIndex")}>
                                            RPI {sortField === "ransomwarePredictionIndex" ? (sortDir === "desc" ? "\u2193" : "\u2191") : ""}
                                        </th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600">Severity</th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600">Assessment</th>
                                        <th className="text-center py-2 px-3 font-medium text-gray-600">Performance</th>
                                        <th className="text-right py-2 px-3 font-medium text-gray-600"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedVendors.map((v) => (
                                        <tr key={v.vendorId} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                            onClick={() => navigate(`/VendorDashboard/${v.vendorId}`)}>
                                            <td className="py-3 px-3">
                                                <div className="font-medium text-content-primary">{v.vendorName}</div>
                                                {v.vendorDomain && <div className="text-xs text-gray-500">{v.vendorDomain}</div>}
                                            </td>
                                            <td className="py-3 px-3 text-center">
                                                {v.vendorTier ? (
                                                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${getTierBadgeColor(v.vendorTier)}`}>
                                                        Tier {v.vendorTier}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">--</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-3 text-center">
                                                {v.notAssessed ? (
                                                    <span className="text-xs text-gray-400">--</span>
                                                ) : (
                                                <div className="flex items-center justify-center gap-2">
                                                    <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                                        <div className={`h-1.5 rounded-full ${
                                                            v.riskLevel === "CRITICAL" ? "bg-red-700" :
                                                            v.riskLevel === "HIGH" ? "bg-orange-500" :
                                                            v.riskLevel === "MEDIUM" ? "bg-yellow-500" : "bg-green-500"
                                                        }`} style={{ width: `${v.riskScorePrecise ?? v.riskScore}%` }}></div>
                                                    </div>
                                                    <span className="font-medium">{(v.riskScorePrecise ?? v.riskScore ?? 0).toFixed(1)}%</span>
                                                </div>
                                                )}
                                            </td>
                                            <td className="py-3 px-3 text-center font-medium">{v.totalRisks}</td>
                                            <td className="py-3 px-3 text-center font-medium text-emerald-700">
                                                {v.totalExpectedAnnualLoss > 0 ? formatCurrency(v.totalExpectedAnnualLoss) : "--"}
                                            </td>
                                            <td className="py-3 px-3 text-center font-medium text-indigo-700">
                                                {v.axarisVendorImpactIndex != null ? `${formatCurrency(v.axarisVendorImpactIndex)}+` : "--"}
                                            </td>
                                            <td className="py-3 px-3 text-center font-medium text-orange-700">
                                                {v.ransomwarePredictionIndex != null ? `${(v.ransomwarePredictionIndex * 100).toFixed(1)}%` : "--"}
                                            </td>
                                            <td className="py-3 px-3">
                                                <div className="flex gap-1 justify-center">
                                                    {v.criticalCount > 0 && <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded">{v.criticalCount}C</span>}
                                                    {v.highCount > 0 && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">{v.highCount}H</span>}
                                                    {v.mediumCount > 0 && <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">{v.mediumCount}M</span>}
                                                    {v.lowCount > 0 && <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded">{v.lowCount}L</span>}
                                                </div>
                                            </td>
                                            <td className="py-3 px-3 text-center">
                                                {/* Assessment status prominent, due/completed date as a small caption
                                                    (same layout rule as the Performance cell). */}
                                                {v.assessmentStatus === "Completed" ? (
                                                    <div className="flex flex-col items-center gap-0.5">
                                                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${ASSESS_STATUS_CFG.Completed.cls}`}>
                                                            Completed
                                                        </span>
                                                        {v.assessmentCompletedDate && (
                                                            <span className="text-[10px] text-gray-400">on {formatShortDate(v.assessmentCompletedDate)}</span>
                                                        )}
                                                    </div>
                                                ) : ASSESS_STATUS_CFG[v.assessmentStatus] ? (
                                                    (() => {
                                                        const overdue = v.assessmentDueDate && new Date(v.assessmentDueDate) < new Date();
                                                        const cfg = overdue
                                                            ? { label: "Overdue", cls: "bg-red-100 text-red-800" }
                                                            : ASSESS_STATUS_CFG[v.assessmentStatus];
                                                        return (
                                                            <div className="flex flex-col items-center gap-0.5">
                                                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap ${cfg.cls}`}>
                                                                    {cfg.label}
                                                                </span>
                                                                {v.assessmentDueDate && (
                                                                    <span className={`text-[10px] ${overdue ? "text-red-500 font-medium" : "text-gray-400"}`}>
                                                                        Due {formatShortDate(v.assessmentDueDate)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })()
                                                ) : (
                                                    <span className="text-xs text-gray-400">Not assessed</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-3 text-center">
                                                {v.performanceStatus === "Completed" ? (
                                                    <div className="flex flex-col items-center gap-0.5">
                                                        <span className="font-semibold text-indigo-700">{Number(v.performanceScore).toFixed(1)}/5</span>
                                                        {v.performanceRecommendationLabel && (
                                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${PERF_LABEL_CFG[v.performanceRecommendationLabel] || "bg-gray-100 text-gray-600"}`}>
                                                                {v.performanceRecommendationLabel}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : v.performanceStatus === "Pending" || v.performanceStatus === "Overdue" ? (
                                                    <div className="flex flex-col items-center gap-0.5">
                                                        {/* A new review cycle can be open while a prior score still exists — surface it so the
                                                            score doesn't appear to vanish once the vendor's status flips back to Pending. */}
                                                        {v.performanceScore != null && (
                                                            <span className="font-semibold text-indigo-700">{Number(v.performanceScore).toFixed(1)}/5</span>
                                                        )}
                                                        <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium whitespace-nowrap ${PERF_STATUS_CFG[v.performanceStatus]}`}>
                                                            {v.performanceStatus === "Overdue" ? "Overdue" : "Due"}{v.performanceDueDate ? ` ${new Date(v.performanceDueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400">Not reviewed</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-3 text-right">
                                                {v.notAssessed ? (
                                                    <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-500 whitespace-nowrap">
                                                        Not Assessed
                                                    </span>
                                                ) : (
                                                    <span className={`px-2 py-0.5 text-xs rounded ${getRiskLevelColor(v.riskLevel)}`}>
                                                        {v.riskLevel}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {showNewVendorModal && (
                <TPRMNewVendorModal
                    isOpen={showNewVendorModal}
                    onClose={() => setShowNewVendorModal(false)}
                    dealId={dealId}
                    onVendorCreated={() => {
                        setShowNewVendorModal(false);
                        fetchPortfolio();
                    }}
                />
            )}
        </div>
    );
};

export default CISOPortfolioView;
